import { useState } from 'react';
import { Search, CheckCircle, Clock, AlertCircle, XCircle, Download } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { StatusBadge } from '../../components/ui/status-badge';
import { EarningsAPI } from '../../lib/api';
import { formatCurrency } from '../../lib/formatters';

export default function BatchStatusPage() {
  const [searchType, setSearchType] = useState<'transactionId' | 'batchId'>('transactionId');
  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [batchStatus, setBatchStatus] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSearch = async () => {
    if (!searchValue.trim()) {
      setError('Please enter a transaction ID or batch ID');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const params: any = {};
      if (searchType === 'transactionId') {
        params.transactionId = searchValue;
      } else {
        params.batchId = searchValue;
      }
      
      const response = await EarningsAPI.getBatchStatus(params);
      if (response.success) {
        setBatchStatus(response.data);
      }
    } catch (err: any) {
      console.error('Error fetching batch status:', err);
      setError(err.message || 'Failed to load batch status');
      setBatchStatus(null);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'processing':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'partial':
        return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'failed':
        return 'bg-red-100 text-red-800 border-red-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
      case 'paid':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'processing':
      case 'pending':
        return <Clock className="h-5 w-5 text-blue-600" />;
      case 'partial':
        return <AlertCircle className="h-5 w-5 text-orange-600" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-600" />;
      default:
        return <Clock className="h-5 w-5 text-gray-600" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Batch Status Tracker</h1>
        <p className="text-muted-foreground">Track the status of batch payouts</p>
      </div>

      {/* Search Section */}
      <Card>
        <CardHeader>
          <CardTitle>Search Batch</CardTitle>
          <CardDescription>Enter transaction ID or batch ID to track status</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <select
              value={searchType}
              onChange={(e) => setSearchType(e.target.value as any)}
              className="px-4 py-2 border rounded-md"
            >
              <option value="transactionId">Transaction ID</option>
              <option value="batchId">Batch ID</option>
            </select>
            
            <div className="flex-1 flex gap-2">
              <input
                type="text"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder={`Enter ${searchType === 'transactionId' ? 'Transaction ID' : 'Batch ID'}...`}
                className="flex-1 px-4 py-2 border rounded-md"
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button onClick={handleSearch} disabled={loading}>
                <Search className="h-4 w-4 mr-2" />
                {loading ? 'Searching...' : 'Search'}
              </Button>
            </div>
          </div>

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-600 text-sm">
              {error}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Batch Status Results */}
      {batchStatus && (
        <div className="space-y-6">
          {/* Status Overview */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {getStatusIcon(batchStatus.status)}
                Batch Status: {batchStatus.status.toUpperCase()}
              </CardTitle>
              <CardDescription>Transaction ID: {batchStatus.transactionId}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">Total Amount</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {formatCurrency(batchStatus.totalAmount)}
                  </p>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">Total Transactions</p>
                  <p className="text-2xl font-bold text-gray-600">
                    {batchStatus.totalTransactions}
                  </p>
                </div>

                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">Paid Transactions</p>
                  <p className="text-2xl font-bold text-green-600">
                    {batchStatus.paidTransactions}
                  </p>
                </div>

                <div className="p-4 bg-red-50 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">Failed Transactions</p>
                  <p className="text-2xl font-bold text-red-600">
                    {batchStatus.failedTransactions}
                  </p>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mt-6">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-muted-foreground">Progress</span>
                  <span className="font-medium">
                    {batchStatus.paidTransactions} / {batchStatus.totalTransactions} completed
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className="bg-green-600 h-3 rounded-full transition-all"
                    style={{
                      width: `${(batchStatus.paidTransactions / batchStatus.totalTransactions) * 100}%`
                    }}
                  ></div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Transaction Details */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Transaction Details</CardTitle>
                  <CardDescription>Individual transaction status in this batch</CardDescription>
                </div>
                <Button variant="outline" size="sm">
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {batchStatus.earnings && batchStatus.earnings.map((earning: any) => (
                  <div
                    key={earning.paymentId}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      {getStatusIcon(earning.status)}
                      <div>
                        <p className="font-semibold">{earning.entityName}</p>
                        <p className="text-sm text-muted-foreground">
                          {earning.entityType.charAt(0).toUpperCase() + earning.entityType.slice(1)} • 
                          Payment ID: {earning.paymentId}
                        </p>
                      </div>
                    </div>

                    <div className="text-right">
                      <p className="text-lg font-bold">{formatCurrency(earning.amount)}</p>
                      <div className="flex items-center gap-2 justify-end mt-1">
                        <StatusBadge status={earning.status} />
                      </div>
                      {earning.paidAt && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(earning.paidAt).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Status Legend */}
          <Card>
            <CardHeader>
              <CardTitle>Status Legend</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 md:grid-cols-4">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="font-medium">Completed</p>
                    <p className="text-xs text-muted-foreground">All transactions processed</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-blue-600" />
                  <div>
                    <p className="font-medium">Processing</p>
                    <p className="text-xs text-muted-foreground">Batch in progress</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-orange-600" />
                  <div>
                    <p className="font-medium">Partial</p>
                    <p className="text-xs text-muted-foreground">Some transactions failed</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <XCircle className="h-5 w-5 text-red-600" />
                  <div>
                    <p className="font-medium">Failed</p>
                    <p className="text-xs text-muted-foreground">Transaction failed</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Empty State */}
      {!batchStatus && !loading && !error && (
        <Card>
          <CardContent className="p-12 text-center">
            <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-lg font-semibold mb-2">Search for a Batch</p>
            <p className="text-muted-foreground">
              Enter a transaction ID or batch ID above to view the status
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
